import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, TrendingUp, Coins, Receipt, Store, AlertTriangle, ArrowRight, Check, Star } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RestaurantDashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(true);
  const [activityDone, setActivityDone] = useState(true);

  // Inline profile completion
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [regAddress, setRegAddress] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [iban, setIban] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/restaurant/dashboard`, { withCredentials: true });
      setDashboard(res.data);
      setProfileComplete(res.data.is_profile_complete !== false);
      axios.get(`${API}/business/activity`, { withCredentials: true }).then(r => setActivityDone(r.data.is_completed)).catch(() => {});
    } catch (e) {
      if (e.response?.status === 403) {
        const detail = e.response?.data?.detail;
        if (detail === 'PROFILE_INCOMPLETE') {
          setProfileComplete(false);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/business/profile`, {
        company_name: companyName.trim(),
        registered_address: regAddress.trim(),
        vat_number: vatNumber.replace(/\s/g, ''),
        iban: iban.replace(/\s/g, '').toUpperCase(),
        phone_number: phone.replace(/[\s\-]/g, '')
      }, { withCredentials: true });
      toast.success('Profilo completato!');
      setShowProfileForm(false);
      setProfileComplete(true);
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>
  );

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="restaurant-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">

        {/* Profile Incomplete Banner */}
        {!profileComplete && !showProfileForm && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="profile-incomplete-banner">
            <div className="flex items-center gap-3 flex-1">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-semibold text-sm text-amber-800 font-sans">Profilo incompleto</p>
                <p className="text-xs text-amber-700 font-sans mt-0.5">{t('cp_banner_text')}</p>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowProfileForm(true)} className="rounded-full btn-lift shrink-0" data-testid="complete-profile-btn">
              Completa profilo <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        )}

        {profileComplete && !activityDone && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="activity-incomplete-banner">
            <div className="flex items-center gap-3 flex-1">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-semibold text-sm text-amber-800 font-sans">{t('act_incomplete_title')}</p>
                <p className="text-xs text-amber-700 font-sans mt-0.5">{t('act_incomplete_text')}</p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate('/restaurant/profile')} className="rounded-full btn-lift shrink-0" data-testid="activity-incomplete-btn">
              {t('act_cta')} <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        )}

        {/* Inline Profile Form */}
        {showProfileForm && (
          <Card className="border border-primary/20 rounded-2xl mb-6 bg-primary/5" data-testid="inline-profile-form">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('cp_title')}</h3>
              <form onSubmit={handleSaveProfile} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Ragione Sociale *</Label>
                  <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="es. Trattoria Bella S.r.l." className="rounded-lg" data-testid="profile-company" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Indirizzo Sede Legale *</Label>
                  <Input value={regAddress} onChange={e => setRegAddress(e.target.value)} placeholder="es. Via Roma 42, Milano" className="rounded-lg" data-testid="profile-address" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Partita IVA *</Label>
                  <Input value={vatNumber} onChange={e => setVatNumber(e.target.value)} placeholder="11 cifre" className="rounded-lg" data-testid="profile-vat" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">IBAN *</Label>
                  <Input value={iban} onChange={e => setIban(e.target.value)} placeholder="IT..." className="rounded-lg" data-testid="profile-iban" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Telefono *</Label>
                  <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+39..." className="rounded-lg" data-testid="profile-phone" />
                </div>
                <div className="flex items-end gap-2">
                  <Button type="submit" disabled={saving} className="rounded-xl btn-lift" data-testid="save-profile-btn">
                    {saving ? '...' : 'Salva'} {!saving && <Check className="w-4 h-4 ml-1" />}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowProfileForm(false)} className="rounded-xl">Annulla</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {dashboard?.restaurant ? (
          <>
            <div className="flex items-start justify-between mb-6 sm:mb-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">Ristorante</p>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {dashboard.restaurant?.name}
                </h1>
                <p className="text-muted-foreground text-sm mt-1 font-sans">{dashboard.restaurant?.address}</p>
              </div>
              {profileComplete && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button data-testid="go-received-reviews-btn" variant="outline" onClick={() => navigate('/restaurant/received-reviews')} className="rounded-full btn-lift">
                    <Star className="w-4 h-4 mr-2" strokeWidth={1.5} /> Recensioni
                  </Button>
                  <Button data-testid="go-validations-btn" onClick={() => navigate('/restaurant/validations')} className="rounded-full btn-lift">
                    <Receipt className="w-4 h-4 mr-2" strokeWidth={1.5} /> Validazioni
                    {(dashboard.pending_receipts > 0 || dashboard.pending_reviews > 0) && (
                      <Badge className="ml-2 bg-destructive text-destructive-foreground text-xs">{(dashboard.pending_receipts || 0) + (dashboard.pending_reviews || 0)}</Badge>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Metrics — only show if profile complete */}
            {profileComplete ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
                  {[
                    { label: "Clienti via Womio", value: dashboard.metrics?.customer_count || 0, icon: <Users className="w-5 h-5" strokeWidth={1.5} />, color: "text-primary" },
                    { label: "Fatturato generato", value: `EUR ${dashboard.metrics?.total_revenue || 0}`, icon: <TrendingUp className="w-5 h-5" strokeWidth={1.5} />, color: "text-emerald-600" },
                    { label: "Fee piattaforma (3%)", value: `EUR ${dashboard.metrics?.platform_fee || 0}`, icon: <Receipt className="w-5 h-5" strokeWidth={1.5} />, color: "text-amber-600" },
                    { label: "Coin emessi (5%)", value: dashboard.metrics?.total_coins_issued || 0, icon: <Coins className="w-5 h-5" strokeWidth={1.5} />, color: "text-secondary-foreground" },
                    { label: "Coin utilizzati dagli utenti", value: `EUR ${dashboard.metrics?.coins_redeemed || 0}`, icon: <Coins className="w-5 h-5" strokeWidth={1.5} />, color: "text-blue-600" },
                  ].map((m, i) => (
                    <Card key={m.label} className="border border-border rounded-2xl" data-testid={`metric-${i}`}>
                      <CardContent className="p-4 sm:p-5">
                        <div className={`w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-muted flex items-center justify-center ${m.color} mb-2 sm:mb-3`}>{m.icon}</div>
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{m.value}</p>
                        <p className="text-[11px] sm:text-xs text-muted-foreground font-sans mt-1">{m.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="border border-border rounded-2xl" data-testid="net-cost-card">
                  <CardContent className="p-5 sm:p-6 space-y-3">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold font-sans">Costo marketing netto</p>
                        <p className="text-xs text-muted-foreground font-sans mt-1">Fee piattaforma + Coin utilizzati dagli utenti</p>
                      </div>
                      <p className="text-2xl font-bold text-primary" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="net-marketing-cost-value">EUR {dashboard.metrics?.net_marketing_cost || 0}</p>
                    </div>
                    <div className="pt-3 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold font-sans">Incidenza costo WOMio su Ricavi WOMio</p>
                        <p className="text-xs text-muted-foreground font-sans mt-1">Costo marketing netto / Fatturato generato</p>
                      </div>
                      <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="womio-cost-incidence-value">{dashboard.metrics?.womio_cost_incidence || 0}%</p>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border border-border rounded-2xl">
                <CardContent className="p-8 text-center">
                  <Store className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-muted-foreground font-sans text-sm">Completa il profilo per vedere le metriche finanziarie e validare gli scontrini.</p>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <div className="text-center py-16 sm:py-20">
            <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Benvenuto su Womio</h2>
            <p className="text-muted-foreground font-sans mb-6 text-sm">{t('cp_start')}</p>
            <Button data-testid="start-profile-btn" onClick={() => setShowProfileForm(true)} className="rounded-full btn-lift">
              Completa profilo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
